import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUploadedFiles } from '../UploadedFilesContext';
import { VENDORS } from '../vendor/vendors';

interface Partner {
  key: string;
  name: string;
  code: string;
  path?: string; // 설정된 경우 클릭 시 해당 라우트로 이동
  excludeRules?: { triggerKeywords: string[]; excludeKeywords: string[] }[];
}

// 파트너 정의는 vendors.ts 중앙 레지스트리에서 파생 — 파트너 추가 시 자동 반영.
// custom 파트너(머드네농장)는 주문서 페이지가 없어 path 를 부여하지 않는다.
const PARTNERS: Partner[] = VENDORS.map(v => ({
  key: v.key,
  name: v.name,
  code: v.code,
  path: v.custom ? undefined : `/admin/${v.key}`,
  excludeRules: v.excludeRules,
}));

interface PartnerRow {
  productOrderNumber: string;
  recipientName: string;
  recipientPhone: string;
  address: string;
  optionInfo: string;
  quantity: number;
  memo: string;
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` };
}

export default function DailyOrders() {
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [filenames, setFilenames] = useState<string[]>([]);
  const [results, setResults] = useState<Record<string, PartnerRow[]>>({});
  const [selected, setSelected] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { password, setFiles: setUploadedFiles, setPassword } = useUploadedFiles();

  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setLoading(true);
    setFilenames(files.map(f => f.name));
    setUploadedFiles(files);

    const byPartner: Record<string, PartnerRow[]> = {};
    for (const partner of PARTNERS) {
      byPartner[partner.code] = [];
    }
    const errors: string[] = [];

    for (const file of files) {
      await Promise.all(PARTNERS.map(async (partner) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('password', password);
        fd.append('vendorCode', partner.code);
        if (partner.excludeRules) fd.append('excludeRules', JSON.stringify(partner.excludeRules));
        try {
          const res = await fetch('/api/vendor/extract', { method: 'POST', body: fd, headers: authHeaders() });
          if (!res.ok) {
            const msg = await res.text();
            errors.push(`${partner.name} (${file.name}): HTTP ${res.status} ${msg.slice(0, 120)}`);
            return;
          }
          const data = await res.json();
          byPartner[partner.code].push(...(data.rows || []));
        } catch (e: any) {
          errors.push(`${partner.name} (${file.name}): ${e?.message || e}`);
        }
      }));
    }

    if (errors.length > 0) {
      alert('일부 파트너 추출 실패:\n\n' + errors.join('\n'));
    }

    setResults(byPartner);
    setLoading(false);
    const firstWithRows = PARTNERS.find(p => (byPartner[p.code] || []).length > 0);
    if (firstWithRows) setSelected(firstWithRows.code);
  };

  const selectedRows = selected ? (results[selected] || []) : [];
  const selectedPartner = PARTNERS.find(p => p.code === selected);

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-800 mb-6">오늘의 주문서</h1>

      {/* 업로드 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-end gap-4 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">원본 비밀번호</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-32 focus:outline-none focus:ring-1 focus:ring-jeju-brown" />
          </div>
          {filenames.length > 0 && (
            <div className="text-xs text-gray-500">업로드: <span className="text-gray-700">{filenames.join(', ')}</span></div>
          )}
        </div>
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); const fs = Array.from(e.dataTransfer.files); if (fs.length) processFiles(fs); }}
          onClick={() => fileRef.current?.click()}
          className={`flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
            dragOver ? 'border-jeju-brown bg-jeju-brown/5' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input ref={fileRef} type="file" accept=".xlsx,.xls" multiple className="hidden" onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) processFiles(fs); e.target.value = ''; }} />
          {loading ? (
            <p className="text-sm text-gray-500">처리 중...</p>
          ) : (
            <>
              <div className="text-3xl mb-2 opacity-30">📊</div>
              <p className="text-sm text-gray-400">스마트스토어 원본 파일을 드래그하거나 클릭하여 선택하세요 (여러 개 가능)</p>
              <p className="text-xs text-gray-300 mt-1">모든 파트너 주문을 한 번에 체크합니다</p>
            </>
          )}
        </div>
      </div>

      {/* 파트너별 현황 */}
      {Object.keys(results).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200 text-sm font-semibold text-gray-700">파트너별 주문 현황</div>
          <div className="divide-y divide-gray-100">
            {PARTNERS.map(p => {
              const count = (results[p.code] || []).length;
              const isSelected = selected === p.code;
              return (
                <button
                  key={p.key}
                  onClick={() => {
                    if (count === 0) return;
                    if (p.path) {
                      navigate(p.path);
                    } else {
                      setSelected(p.code);
                    }
                  }}
                  disabled={count === 0}
                  className={`w-full flex items-center justify-between px-6 py-3 text-sm transition-colors text-left ${
                    isSelected ? 'bg-jeju-brown/5' : 'hover:bg-gray-50'
                  } ${count === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-medium ${isSelected ? 'text-jeju-brown' : 'text-gray-800'}`}>{p.name}</span>
                    <span className="text-xs text-gray-400 font-mono">{p.code}-</span>
                    {p.path && count > 0 && <span className="text-[10px] text-gray-400">→ 주문서 페이지로 이동</span>}
                  </div>
                  <span className={`text-sm font-semibold ${count > 0 ? 'text-gray-800' : 'text-gray-400'}`}>
                    {count > 0 ? `${count}건` : '주문 없음'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 선택한 파트너 주문 미리보기 */}
      {selectedPartner && selectedRows.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-800">{selectedPartner.name} 주문 미리보기</div>
              <div className="text-xs text-gray-400 mt-0.5">총 {selectedRows.length}건</div>
            </div>
          </div>
          <div className="overflow-auto max-h-[calc(100vh-520px)]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">받는사람</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">연락처</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">주소</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">옵션정보</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">수량</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {selectedRows.map((o, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-gray-800 font-medium whitespace-nowrap">{o.recipientName}</td>
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{o.recipientPhone}</td>
                    <td className="px-4 py-2.5 text-gray-600 max-w-[240px] truncate" title={o.address}>{o.address}</td>
                    <td className="px-4 py-2.5 text-gray-700 max-w-[280px] truncate" title={o.optionInfo}>{o.optionInfo}</td>
                    <td className="px-4 py-2.5 text-gray-800 text-right">{o.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
